import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import HomeIcon from '@mui/icons-material/Home';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import TimelineIcon from '@mui/icons-material/Timeline';
import {
  EMPLOYEE_STATUSES, EMPLOYMENT_TYPES,
  EMPLOYEE_STATUS_COLORS, formatEnumLabel
} from '../../../../constants/hrm';
import { useEmployee, useUpdateEmployee, useDepartments, usePositions, useLifecycleEvents } from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const eventTypeColors = {
  HIRED: 'success',
  PROMOTED: 'info',
  TRANSFERRED: 'primary',
  TERMINATED: 'error',
  RESIGNED: 'warning',
  ON_LEAVE: 'warning',
  RETURNED: 'success',
  PROBATION_END: 'info',
};

const EmployeeDetail = () => {
  const { chatbotId, employeeId } = useParams();
  const navigate = useNavigate();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  // React Query hooks for data fetching
  const { data: employeeData, isLoading: loading } = useEmployee(chatbotId, employeeId);
  const employee = employeeData?.data || null;

  const { data: lifecycleData } = useLifecycleEvents(chatbotId, { employee_id: employeeId, per_page: 50 });
  const lifecycleEvents = lifecycleData?.data || [];

  const { data: departmentsData } = useDepartments(chatbotId, { per_page: 1000 });
  const departments = departmentsData?.data || [];

  const { data: positionsData } = usePositions(chatbotId, { per_page: 1000 });
  const positions = positionsData?.data || [];

  // Mutation hook for updating employee
  const updateEmployee = useUpdateEmployee(chatbotId);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const handleOpenEdit = () => {
    if (!employee) return;
    setFormData({
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department_id: employee.department_id || '',
      position_id: employee.position_id || '',
      employment_type: employee.employment_type || 'FULL_TIME',
      hire_date: employee.hire_date ? employee.hire_date.split('T')[0] : '',
      status: employee.status || 'ACTIVE',
      employee_id_number: employee.employee_id_number || '',
      address_line1: employee.address_line1 || '',
      address_line2: employee.address_line2 || '',
      city: employee.city || '',
      state: employee.state || '',
      postal_code: employee.postal_code || '',
      country: employee.country || '',
      emergency_contact_name: employee.emergency_contact_name || '',
      emergency_contact_phone: employee.emergency_contact_phone || '',
      emergency_contact_relationship: employee.emergency_contact_relationship || '',
    });
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setEditDialogOpen(false);
    setFormData({});
    setFormErrors({});
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name?.trim()) errors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) errors.last_name = 'Last name is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const payload = { ...formData };

      // Convert empty strings to null
      const optionalFields = [
        'department_id', 'position_id', 'phone', 'hire_date', 'employee_id_number',
        'address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country',
        'emergency_contact_name', 'emergency_contact_phone', 'emergency_contact_relationship',
      ];
      optionalFields.forEach((field) => {
        if (!payload[field]) payload[field] = null;
      });

      await updateEmployee.mutateAsync({ id: employeeId, ...payload });
      handleCloseEdit();
    } catch (error) {
      console.error('Error updating employee:', error);
      if (error.response?.data?.message) {
        setFormErrors({ general: error.response.data.message });
      }
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          You do not have permission to view employee details.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!employee) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Employee not found.
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/hrm/employees`)}
          sx={{ mt: 2 }}
        >
          Back to Employees
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/hrm/employees`)}
            size="small"
          >
            Back
          </Button>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            {employee.first_name?.[0]}{employee.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {employee.first_name} {employee.last_name}
            </Typography>
            {employee.employee_id_number && (
              <Typography variant="body2" color="textSecondary">
                ID: {employee.employee_id_number}
              </Typography>
            )}
          </Box>
          <Chip
            label={formatEnumLabel(employee.status)}
            color={EMPLOYEE_STATUS_COLORS[employee.status] || 'default'}
          />
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleOpenEdit} size="small">
            Edit Employee
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Personal Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Personal Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">First Name</Typography>
                  <Typography variant="body1">{employee.first_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Last Name</Typography>
                  <Typography variant="body1">{employee.last_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{employee.email}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{employee.phone || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                  <Typography variant="body1">
                    {employee.date_of_birth
                      ? new Date(employee.date_of_birth).toLocaleDateString()
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Gender</Typography>
                  <Typography variant="body1">{employee.gender || '-'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Employment Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WorkIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Employment Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Department</Typography>
                  <Typography variant="body1">{employee.department_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Position</Typography>
                  <Typography variant="body1">{employee.position_title || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Employment Type</Typography>
                  <Typography variant="body1">
                    {formatEnumLabel(employee.employment_type)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip
                    label={formatEnumLabel(employee.status)}
                    color={EMPLOYEE_STATUS_COLORS[employee.status] || 'default'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Hire Date</Typography>
                  <Typography variant="body1">
                    {employee.hire_date
                      ? new Date(employee.hire_date).toLocaleDateString()
                      : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Employee ID</Typography>
                  <Typography variant="body1">{employee.employee_id_number || '-'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <HomeIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Address
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Address Line 1</Typography>
                  <Typography variant="body1">{employee.address_line1 || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Address Line 2</Typography>
                  <Typography variant="body1">{employee.address_line2 || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">City</Typography>
                  <Typography variant="body1">{employee.city || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">State</Typography>
                  <Typography variant="body1">{employee.state || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Postal Code</Typography>
                  <Typography variant="body1">{employee.postal_code || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Country</Typography>
                  <Typography variant="body1">{employee.country || '-'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Emergency Contact */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ContactPhoneIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Emergency Contact
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Name</Typography>
                  <Typography variant="body1">{employee.emergency_contact_name || '-'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{employee.emergency_contact_phone || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Relationship</Typography>
                  <Typography variant="body1">
                    {employee.emergency_contact_relationship || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Lifecycle Events Timeline */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimelineIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Lifecycle Events
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {lifecycleEvents.length > 0 ? (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 700 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Event Type</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>From Department</TableCell>
                        <TableCell>To Department</TableCell>
                        <TableCell>From Position</TableCell>
                        <TableCell>To Position</TableCell>
                        <TableCell>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lifecycleEvents.map((event) => (
                        <TableRow key={event.id} hover>
                          <TableCell>
                            <Chip
                              label={formatEnumLabel(event.event_type)}
                              color={eventTypeColors[event.event_type] || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {event.event_date
                              ? new Date(event.event_date).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell>{event.description || '-'}</TableCell>
                          <TableCell>{event.from_department_name || '-'}</TableCell>
                          <TableCell>{event.to_department_name || '-'}</TableCell>
                          <TableCell>{event.from_position_title || '-'}</TableCell>
                          <TableCell>{event.to_position_title || '-'}</TableCell>
                          <TableCell>{event.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No lifecycle events recorded
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Typography color="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Personal Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                Personal Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name || ''}
                onChange={handleFormChange('first_name')}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name || ''}
                onChange={handleFormChange('last_name')}
                error={!!formErrors.last_name}
                helperText={formErrors.last_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={handleFormChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone || ''}
                onChange={handleFormChange('phone')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID Number"
                value={formData.employee_id_number || ''}
                onChange={handleFormChange('employee_id_number')}
              />
            </Grid>

            {/* Employment Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                Employment Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department_id || ''}
                  onChange={handleFormChange('department_id')}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Position</InputLabel>
                <Select
                  value={formData.position_id || ''}
                  onChange={handleFormChange('position_id')}
                  label="Position"
                >
                  <MenuItem value="">None</MenuItem>
                  {positions
                    .filter((pos) =>
                      formData.department_id
                        ? pos.department_id === formData.department_id
                        : true
                    )
                    .map((pos) => (
                      <MenuItem key={pos.id} value={pos.id}>
                        {pos.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employment_type || 'FULL_TIME'}
                  onChange={handleFormChange('employment_type')}
                  label="Employment Type"
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatEnumLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'ACTIVE'}
                  onChange={handleFormChange('status')}
                  label="Status"
                >
                  {EMPLOYEE_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {formatEnumLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={formData.hire_date || ''}
                onChange={handleFormChange('hire_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.address_line1 || ''}
                onChange={handleFormChange('address_line1')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.address_line2 || ''}
                onChange={handleFormChange('address_line2')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ''}
                onChange={handleFormChange('city')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.state || ''}
                onChange={handleFormChange('state')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postal_code || ''}
                onChange={handleFormChange('postal_code')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country || ''}
                onChange={handleFormChange('country')}
              />
            </Grid>

            {/* Emergency Contact */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                Emergency Contact
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.emergency_contact_name || ''}
                onChange={handleFormChange('emergency_contact_name')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.emergency_contact_phone || ''}
                onChange={handleFormChange('emergency_contact_phone')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Relationship"
                value={formData.emergency_contact_relationship || ''}
                onChange={handleFormChange('emergency_contact_relationship')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={updateEmployee.isPending}>
            {updateEmployee.isPending ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeDetail;
